// src/components/CattleForm.js
import React, { useState } from 'react';

const EMPTY = {
  dateOfEntry: '', tagNumber: '', tagColor: '', breed: '', age: '',
  gender: '', color: '', identifier: '', disability: '', dateOfExit: '', exitReason: ''
};

const TAG_COLORS = ['YELLOW', 'GREEN', 'RED', 'BLUE', 'WHITE', 'ORANGE'];
const BREEDS = ['INDIGENOUS', 'CROSS', 'HF', 'JERSEY', 'SAHIWAL', 'GEER', 'OTHER'];
const GENDERS = ['MALE', 'FEMALE'];
const EXIT_REASONS = ['', 'DEATH', 'SALE', 'DONATE'];

function validateTag(tag) {
  if (!tag) return 'Tag number is required.';
  if (!/^\d+$/.test(tag)) return 'Tag number must contain digits only.';
  if (tag.length !== 12) return `Must be exactly 12 digits (entered: ${tag.length}).`;
  return null;
}

export default function CattleForm({ initial, onSave, onClose, title }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [tagError, setTagError] = useState('');
  const [saveError, setSaveError] = useState('');

  const set = (field, val) => {
    setForm(f => ({ ...f, [field]: val }));
    if (field === 'tagNumber') setTagError(validateTag(val) || '');
  };

  async function handleSave(e) {
    e.preventDefault();
    setSaveError('');
    const tagErr = validateTag(form.tagNumber);
    if (tagErr) { setTagError(tagErr); return; }
    setSaving(true);
    try {
      await onSave({ ...form, age: Number(form.age) || 0 });
      onClose();
    } catch (err) {
      setSaveError(err.message);
    }
    setSaving(false);
  }

  const tagLen = form.tagNumber.length;
  const tagOk = /^\d{12}$/.test(form.tagNumber);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>{title || 'Cattle Record'}</h2>
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Date of Entry *</label>
              <input type="text" value={form.dateOfEntry} onChange={e => set('dateOfEntry', e.target.value)} placeholder="DD/MM/YY" />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Tag Number * <span style={{ color: '#6b7280', fontWeight: 400 }}>(12 digits)</span></span>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: tagLen === 0 ? '#9ca3af' : tagLen === 12 ? '#16a34a' : '#dc2626'
                }}>{tagLen}/12</span>
              </label>
              <input
                type="text"
                value={form.tagNumber}
                onChange={e => set('tagNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="e.g. 102347020244"
                maxLength={12}
                style={{
                  borderColor: tagLen > 0 ? (tagOk ? '#16a34a' : '#dc2626') : undefined,
                  boxShadow: tagLen > 0 ? ('0 0 0 2px ' + (tagOk ? '#dcfce7' : '#fee2e2')) : undefined,
                }}
              />
              {tagError && (
                <span style={{ fontSize: 12, color: '#dc2626', marginTop: 4, display: 'block' }}>
                  {'\u26a0\ufe0f'} {tagError}
                </span>
              )}
              {tagOk && !tagError && (
                <span style={{ fontSize: 12, color: '#16a34a', marginTop: 4, display: 'block' }}>
                  {'\u2705'} Valid 12-digit tag
                </span>
              )}
            </div>

            <div className="form-group">
              <label>Tag Color</label>
              <select value={form.tagColor} onChange={e => set('tagColor', e.target.value)}>
                <option value="">Select...</option>
                {TAG_COLORS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Breed (नस्ल)</label>
              <select value={form.breed} onChange={e => set('breed', e.target.value)}>
                <option value="">Select...</option>
                {BREEDS.map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Age (years)</label>
              <input type="number" value={form.age} onChange={e => set('age', e.target.value)} min="0" max="30" />
            </div>
            <div className="form-group">
              <label>Gender (नर/मादा)</label>
              <select value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="">Select...</option>
                {GENDERS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Color (गौवंश का रंग)</label>
              <input type="text" value={form.color} onChange={e => set('color', e.target.value)} placeholder="e.g. BLACK WHITE SPOT" />
            </div>
            <div className="form-group">
              <label>Identifier (पहचान)</label>
              <input type="text" value={form.identifier} onChange={e => set('identifier', e.target.value)} placeholder="e.g. TAG NO." />
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label>Disability / Illness (अंधा/विकलांग/बीमार)</label>
              <input type="text" value={form.disability} onChange={e => set('disability', e.target.value)} placeholder="e.g. NaN or PH" />
            </div>
            <div className="form-group">
              <label>Date of Exit (निकास की दिनांक)</label>
              <input type="text" value={form.dateOfExit} onChange={e => set('dateOfExit', e.target.value)} placeholder="DD/MM/YY or blank if alive" />
            </div>
            <div className="form-group">
              <label>Exit Reason (कारण)</label>
              <select value={form.exitReason} onChange={e => set('exitReason', e.target.value)}>
                {EXIT_REASONS.map(r => <option key={r} value={r}>{r || '-- Alive --'}</option>)}
              </select>
            </div>
          </div>

          {saveError && (
            <div style={{
              background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8,
              padding: '10px 14px', marginTop: 8, fontSize: 13, color: '#dc2626', display: 'flex', gap: 8
            }}>
              <span>Duplicate tag:</span><span>{saveError}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !tagOk}>
              {saving ? 'Checking & Saving...' : 'Save Record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
